import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&*!?<>{}[]';

const DecryptText = ({ text, delay = 0, speed = 30, onComplete }) => {
  const [display, setDisplay] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let timeout;
    timeout = setTimeout(() => {
      let iteration = 0;
      const maxIterations = text.length * 3;
      
      const interval = setInterval(() => {
        setDisplay(
          text.split('').map((char, i) => {
            if (char === ' ') return ' ';
            if (i < iteration / 3) return char;
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          }).join('')
        );

        iteration++;
        if (iteration > maxIterations) {
          clearInterval(interval);
          setDisplay(text);
          setDone(true);
          onComplete?.();
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timeout);
  }, [text, delay, speed, onComplete]);

  return (
    <span className={`transition-colors duration-300 ${done ? 'text-primary' : 'text-primary/70'}`}>
      {display || text.split('').map(() => CHARS[Math.floor(Math.random() * CHARS.length)]).join('')}
    </span>
  );
};

const DecryptionLoader = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const [visible, setVisible] = useState(true);

  const lines = [
    { text: '> INITIALIZING ALPHA1-ZERO...', delay: 0 },
    { text: '> LOADING NEURAL NETWORKS...', delay: 600 },
    { text: '> DECRYPTING SECURE CHANNELS...', delay: 1200 },
    { text: '> ESTABLISHING TACTICAL LINK...', delay: 1800 },
    { text: '> SYSTEM ONLINE', delay: 2400 },
  ];

  const handleLineComplete = useCallback((index) => {
    if (index < lines.length - 1) {
      setPhase(index + 1);
    } else {
      setTimeout(() => {
        setVisible(false);
        setTimeout(() => onComplete?.(), 500);
      }, 400);
    }
  }, [lines.length, onComplete]);

  if (!visible) {
    return (
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 z-[100] bg-[#000a00] flex items-center justify-center"
      />
    );
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-[#000a00] flex items-center justify-center"
          data-testid="decryption-loader"
        >
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.03) 2px, rgba(0,255,65,0.03) 4px)',
            }}
          />

          <div className="absolute top-8 left-8 w-12 h-12 border-l-2 border-t-2 border-primary/40" />
          <div className="absolute top-8 right-8 w-12 h-12 border-r-2 border-t-2 border-primary/40" />
          <div className="absolute bottom-8 left-8 w-12 h-12 border-l-2 border-b-2 border-primary/40" />
          <div className="absolute bottom-8 right-8 w-12 h-12 border-r-2 border-b-2 border-primary/40" />

          <div className="max-w-xl w-full px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto mb-4">
                <polygon points="50,2 95,25 95,75 50,98 5,75 5,25" fill="none" stroke="#00FF41" strokeWidth="2" opacity="0.8"/>
                <circle cx="50" cy="50" r="5" fill="#00FF41"/>
                <text x="50" y="58" textAnchor="middle" fill="#00FF41" fontSize="20" fontFamily="Orbitron" fontWeight="bold">α1</text>
              </svg>
              <h1 className="font-military text-2xl tracking-widest text-primary text-glow-primary">
                ALPHA1-ZERO
              </h1>
              <p className="text-[10px] font-mono text-primary/40 tracking-[0.3em] mt-1">SKIDRIVE TECHNOLOGIES</p>
            </motion.div>

            <div className="font-mono text-sm space-y-3">
              {lines.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: i <= phase ? 1 : 0.2, x: i <= phase ? 0 : -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-2"
                >
                  {i <= phase ? (
                    <DecryptText
                      text={line.text}
                      delay={i === phase ? 100 : 0}
                      speed={25}
                      onComplete={() => handleLineComplete(i)}
                    />
                  ) : (
                    <span className="text-primary/20">{line.text}</span>
                  )}
                  {i === phase && (
                    <span className="inline-block w-2 h-4 bg-primary animate-pulse" />
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-8 h-[2px] bg-primary/10 overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: '0%' }}
                animate={{ width: `${((phase + 1) / lines.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-[10px] font-mono text-primary/30 mt-2 text-right tracking-wider">
              {Math.round(((phase + 1) / lines.length) * 100)}% COMPLETE
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DecryptionLoader;
